import { VsPeerState } from "../valuation-page/valuation-page.model";
import {
  ABS_PERIOD_TYPE,
  convertPeriodMetaToUserSettings,
} from "@va/core-library/period-filter";
import { TrendState, TabSettingsJson } from "./comparative-analysis.model";
import {
  getSelectedRatiosId,
  getIsDenomSelected,
  getIsDenomYoySelected,
} from "src/app/valuation-page/valuation-page-utils";
import { getSelectedMarketIdFromList } from "src/app/comps-page/utils";
import {
  CHART_TYPE_DATA,
  USER_SETTINGS_LINE_ID,
  TAB_BE_MAPPING,
} from "./constants";

export function createDataCallUrlForMcapEvLp(
  state: VsPeerState,
  optimizedTickerSet: Array<any> = [],
  maxParallelCalls,
): Array<string> {
  const apiType = ["price", "mcap", "ev"];
  const marketDataMapping = {
    price: "10",
    mcap: "11",
    ev: "34",
  };
  const urls: Array<string> = [];
  let tickerUrl = "";
  const dataUrlList: Array<string> = [];
  let startPoint = 0;
  const increment = Math.ceil(optimizedTickerSet.length / maxParallelCalls);

  for (let i = 0; i < maxParallelCalls; i++) {
    const tickerSetChunks = optimizedTickerSet.slice(
      startPoint,
      startPoint + increment,
    );
    tickerUrl = tickerSetChunks.map((t) => t.ticker).join("&ticker=");
    let dataUrl = `source=Consensus&viewBy=values&ticker=${tickerUrl}`;

    if (state.currency !== "Base") {
      dataUrl += `&currency=${state.currency}`;
    }
    dataUrlList.push(dataUrl);
    startPoint += increment;
  }

  dataUrlList.forEach((dataUrl) => {
    apiType.forEach((api) => {
      const updatedUrl = `${dataUrl}&api_type=${api}&market_data=${marketDataMapping[api]}`;
      urls.push(updatedUrl.trim());
    });
  });
  return urls;
}

export function getTreeFilterdData(searchTerm, data = []) {
  let output = [];
  const formattedSearchTerm = searchTerm && searchTerm.trim().toLowerCase();
  if (data && data.length) {
    if (formattedSearchTerm) {
      for (const item of data) {
        const inddustryResult = [];
        if (item.children && item.children.length) {
          for (const levelTwoItem of item.children) {
            const subgroupResult = [];
            const formattedLevelTwoTitle = levelTwoItem.title
              .trim()
              .toLowerCase();
            if (formattedLevelTwoTitle.includes(formattedSearchTerm)) {
              inddustryResult.push({
                ...levelTwoItem,
                selected: false,
                expanded: true,
              });
              continue;
            }
            if (levelTwoItem.children && levelTwoItem.children.length) {
              for (const levelThreeItem of levelTwoItem.children) {
                const formattedLevelThreeTitle = levelThreeItem.title
                  .trim()
                  .toLowerCase();
                if (formattedLevelThreeTitle.includes(formattedSearchTerm)) {
                  subgroupResult.push(levelThreeItem);
                }
              }
              if (subgroupResult.length) {
                inddustryResult.push({
                  ...levelTwoItem,
                  selected: false,
                  expanded: true,
                  children: subgroupResult,
                });
              }
            }
          }
          if (inddustryResult.length) {
            output.push({
              ...item,
              selected: false,
              expanded: true,
              children: inddustryResult,
            });
          }
        }
      }
    } else {
      output = cloneThreeLevelTree(data);
    }
  }
  return output;
}

export function getListFilterdData(searchTerm, data = []) {
  let output = [];
  const formattedSearchTerm = searchTerm && searchTerm.trim().toLowerCase();
  if (data && data.length) {
    if (formattedSearchTerm) {
      for (const item of data) {
        const formattedTitle = item.label.trim().toLowerCase();
        if (formattedTitle.includes(formattedSearchTerm)) {
          output.push(item);
        }
      }
    } else {
      output = [...data];
    }
  }
  return output;
}

export function getMetaPayloadFromUrls(
  paramData,
  queryData,
  tabType,
  compType,
  state?,
  chartType?,
) {
  const tab_type = tabType;
  const comp_type = compType;
  const id = parseInt(paramData.compId || queryData.id) || undefined;
  const sub_id = queryData.sub_id ? [parseInt(queryData.sub_id)] : undefined;
  const comps_name = queryData.comps_name ? queryData.comps_name : undefined;
  const market_cap = queryData.market_cap
    ? [].concat(queryData.market_cap).map((item) => parseInt(item))
    : [-1];
  const region_id = queryData.region_id
    ? [].concat(queryData.region_id).map((item) => parseInt(item))
    : queryData.country_id
      ? []
      : undefined;
  const country_id = queryData.country_id
    ? [].concat(queryData.country_id).map((item) => parseInt(item))
    : undefined;

  const tickers =
    state && state.tickers
      ? state.tickers.map((tickerObj) => tickerObj.ticker)
      : queryData.tickers
        ? [].concat(queryData.tickers)
        : undefined;
  const uniquesliid =
    state && state.lineItem
      ? tab_type === TAB_BE_MAPPING.revision ||
        tab_type === TAB_BE_MAPPING.surprise
        ? [state.lineItem.uniquesliid]
        : state.lineItem.map((item) => {
            return item.uniquesliid;
          })
      : queryData.uniquesliids
        ? [].concat(queryData.uniquesliids).map((item) => parseInt(item))
        : undefined;
  const period =
    state && state.periodLabel
      ? state.periodLabel
      : queryData.period
        ? [].concat(queryData.period)
        : undefined;
  let periodDetailsObj = {};

  const index_to_100 =
    state && state.isIndexToHundreadSelected
      ? state.isIndexToHundreadSelected
      : true;

  if (period) {
    const period_type =
      state && state.periodType
        ? state.periodType
        : queryData.period_type || ABS_PERIOD_TYPE.id;
    const isAnnualYearSelected =
      queryData.is_annual_selected !== undefined
        ? queryData.is_annual_selected
        : !!(period && period.length);
    const is_annual_selected = isAnnualYearSelected;

    periodDetailsObj = {
      period,
      is_annual_selected,
      period_type,
    };
  }

  const other_period =
    state && state.otherPeriod ? state.otherPeriod : queryData.other_period;
  const currency =
    state && state.currency ? state.currency : queryData.currency;
  const source =
    state && state.source
      ? state.source
      : queryData.source || "VA Actuals/Consensus";
  const revision =
    state && state.revision ? state.revision : queryData.revision;
  const tab_name = (state && state.pageName) || queryData.tab_name;
  const tpr_price_performance = state?.pricePerformance;
  const tpr_selected_columns = state?.columnPickerSelectedColumns;

  const chart_type = chartType || undefined;
  if (comp_type === "user") {
    return {
      id,
      comp_type,
      tab_type,
      chart_type,
    };
  }
  const suggested_tickers = comp_type === "trend" && tab_type === "Trend";
  return {
    id,
    sub_id,
    comps_name,
    comp_type,
    tab_type,
    uniquesliid,
    tickers,
    tab_name,
    ...periodDetailsObj,
    other_period,
    currency,
    revision,
    source,
    market_cap,
    region_id,
    country_id,
    tickers_suggestion: suggested_tickers || undefined,
    chart_type,
    tpr_price_performance,
    tpr_selected_columns,
    index_to_100,
  };
}

export function cloneThreeLevelTree(data) {
  return data.map((item) => ({
    ...item,
    selected: false,
    children: item.children
      ? item.children.map((secondItem) => ({
          ...secondItem,
          selected: false,
          children: secondItem.children
            ? secondItem.children.map((thirdItem) => ({
                ...thirdItem,
                selected: false,
              }))
            : [],
        }))
      : [],
  }));
}

export function getCompanyId(tickerList) {
  return tickerList.map(
    (item) => item.company_id || item.cid || item.companyid || item.ticker,
  );
}

export function getUsliId(lineitemList) {
  return lineitemList.map((item) => item.uniquesliid);
}

export function createTrendCompTabUserSettings(state: TrendState, tickerCid) {
  const result = {
    Ticker: {
      cids: tickerCid,
    },
    LineItem: {
      line_ids: state.selectedLineItemId,
    },
    Period: state.periodUserSetting,
    DataView: {
      view_by: state.viewBy,
      revision: state.revision,
    },
    Currency: { currency_iso: state.currency },
    Source: { source_name: state.source },
    ShowBlank: { show_blank: state.showBlankRows || false },
  };
  if (state.marketItemsData) {
    const selectedMarcketIds = getSelectedMarketIdFromList(
      state.marketItemsData,
    );
    if (selectedMarcketIds?.length) {
      result.LineItem["market_data"] = selectedMarcketIds;
    }
  }
  return result;
}

export function createChartsTabUserSettings(state, tickerCid, isComparison) {
  let result = null;
  const lineItemPropetyToUpdate = !isComparison
    ? "charts_rev_trend_lids"
    : "charts_bar_lids";
  if (state.tickers && state.lineItem) {
    result = {
      Ticker: {
        cids: tickerCid,
        chart_ticker_selected_type: state.tickerSelectionType,
        chart_custom_cid: state.customTickers,
        chart_include_average: state.tickerIncludeAverageSelected || false,
      },
      [lineItemPropetyToUpdate]: state.selectedLineItemId,
      Period: state.periodUserSetting,
      Currency: { currency_iso: state.currency },
      DataView: {
        view_by: state.viewBy,
        revision: state.revision,
      },
      IndexTo100: {
        index_to_100: state.isIndexToHundreadSelected,
      },
    };
  }
  return result;
}

export function createValuationTabUserSettings(
  state,
  tickerCid,
  shouldAddOrientation = true,
) {
  let result = null;
  if (state.tickers && state.ratios) {
    const selectedRatioId = getSelectedRatiosId(state.ratios.ratio_details);
    const denom = getIsDenomSelected(state.ratios.include);
    const denomYoy = getIsDenomYoySelected(state.ratios.include);
    result = {
      Ticker: {
        cids: tickerCid,
      },
      LineItem: {
        ratios: {
          names: selectedRatioId,
          denom: denom,
          denom_yoy: denomYoy,
        },
      },
      Period: state.periodUserSetting,
      Currency: { currency_iso: state.currency },
    };
    if (shouldAddOrientation) {
      result = {
        ...result,
        Orientation: { orientation: state.orientationValue },
      };
    }
  }

  return result;
}

export function createTPRTabUserSettings(state, tickerCid) {
  let result = null;
  if (state.tickers && state.columnPickerSelectedColumns) {
    result = {
      Ticker: {
        cids: tickerCid,
      },
      LineItem: {
        tpr_grid_columns: state.columnPickerSelectedColumns,
        price_performance: state.pricePerformance,
      },
      Currency: {
        currency_iso: state.currency,
      },
    };
  }
  return result;
}

export function createRevSurTabUserSettings(state, tickerCid, tabType) {
  let result = null;
  if (state.tickers && state.selectedLineItemId) {
    const lineIdProperty = USER_SETTINGS_LINE_ID[tabType];

    result = {
      Ticker: {
        cids: tickerCid,
      },
      LineItem: {
        [lineIdProperty]: state.selectedLineItemId,
      },
      Currency: {
        currency_iso: state.currency,
      },
      ShowBlank: { show_blank: state.showBlankRows || false },
    };
  }
  return result;
}

export function createJsonForSaveAsFeature(
  headerTitle,
  trendState?,
  comparisonState?,
  valuationState?,
  chartsTrendState?,
  chartsComparisonState?,
  chartsRevisionState?,
  tprState?,
  revisionState?,
  surpriseState?,
) {
  const tabdata: TabSettingsJson = {};
  let tickerCid = null;
  if (trendState) {
    if (!tickerCid && trendState.tickers) {
      tickerCid = getCompanyId(trendState.tickers);
    }
    tabdata.Trend = createTrendCompTabUserSettings(trendState, tickerCid);
  }
  if (comparisonState) {
    if (!tickerCid && comparisonState.tickers) {
      tickerCid = getCompanyId(comparisonState.tickers);
    }
    tabdata.Comparison = createTrendCompTabUserSettings(
      comparisonState,
      tickerCid,
    );
  }
  if (valuationState) {
    if (!tickerCid && valuationState.tickers) {
      tickerCid = getCompanyId(valuationState.tickers);
    }
    const data = createValuationTabUserSettings(valuationState, tickerCid);
    if (data) {
      tabdata.Valuation = data;
    }
  }

  if (chartsTrendState) {
    if (!tickerCid && chartsTrendState.tickers) {
      tickerCid = getCompanyId(chartsTrendState.tickers);
    }
    const data = createChartsTabUserSettings(
      chartsTrendState,
      tickerCid,
      false,
    );
    if (data) {
      tabdata.Charts_Trend = data;
    }
  }
  if (chartsComparisonState) {
    if (!tickerCid && chartsComparisonState.tickers) {
      tickerCid = getCompanyId(chartsComparisonState.tickers);
    }
    const data = createChartsTabUserSettings(
      chartsComparisonState,
      tickerCid,
      true,
    );
    if (data) {
      tabdata.Charts_Bar = data;
    }
  }
  if (chartsRevisionState) {
    if (!tickerCid && chartsRevisionState.tickers) {
      tickerCid = getCompanyId(chartsRevisionState.tickers);
    }
    const data = createChartsTabUserSettings(
      chartsRevisionState,
      tickerCid,
      false,
    );
    if (data) {
      tabdata.Charts_Revision = data;
    }
  }

  if (tprState) {
    if (!tickerCid && tprState.tickers) {
      tickerCid = getCompanyId(tprState.tickers);
    }
    const data = createTPRTabUserSettings(tprState, tickerCid);
    if (data) {
      tabdata.TPR = data;
    }
  }

  if (revisionState) {
    if (!tickerCid && revisionState.tickers) {
      tickerCid = getCompanyId(revisionState.tickers);
    }
    const data = createRevSurTabUserSettings(
      revisionState,
      tickerCid,
      TAB_BE_MAPPING.revision,
    );
    if (data) {
      tabdata.Revision = data;
    }
  }

  if (surpriseState) {
    if (!tickerCid && surpriseState.tickers) {
      tickerCid = getCompanyId(surpriseState.tickers);
    }
    const data = createRevSurTabUserSettings(
      surpriseState,
      tickerCid,
      TAB_BE_MAPPING.surprise,
    );
    if (data) {
      tabdata.Surprise = data;
    }
  }

  return {
    comps_name: headerTitle,
    tab_data: tabdata,
  };
}

export function createJsonForCreateNewFeature(data) {
  const dataToUpdate = {
    Ticker: {
      cids: getCompanyId(data.addRowTableData),
    },
    LineItem: {
      line_ids: getUsliId(data.addColTableData),
    },
    Period: convertPeriodMetaToUserSettings(data.yearTypeData),
    Source: { source_name: "VA Actuals/Consensus" },
  };
  if (data.marketItemsData) {
    const selectedMarcketIds = getSelectedMarketIdFromList(
      data.marketItemsData,
    );
    if (selectedMarcketIds?.length) {
      dataToUpdate.LineItem["market_data"] = selectedMarcketIds;
    }
  }
  const tabdata: TabSettingsJson = {
    Trend: dataToUpdate,
  };

  return {
    comps_name: data.tabName,
    tab_data: tabdata,
  };
}

export function updateCustomTableSettings(compId, tabType, tabDataToUpdate) {
  const tabObj = {
    [tabType]: tabDataToUpdate,
  };
  return {
    cc_id: typeof compId === "string" ? Number(compId) : undefined,
    tab_data: tabObj,
  };
}

export function updateCustomTableSettingsForTickers(
  compId,
  tabType,
  tabDataToUpdate,
) {
  const tickerToUpdateForCharts = {
    Ticker: {
      cids: tabDataToUpdate.Ticker.cids,
      chart_ticker_selected_type: "top_ten",
      chart_custom_cid: [],
    },
  };
  const tabObj = {
    [tabType]: tabDataToUpdate,
    [CHART_TYPE_DATA.trend.meta_id]: tickerToUpdateForCharts,
    [CHART_TYPE_DATA.comparison.meta_id]: tickerToUpdateForCharts,
    [CHART_TYPE_DATA.revision.meta_id]: tickerToUpdateForCharts,
  };
  return {
    cc_id: typeof compId === "string" ? Number(compId) : undefined,
    tab_data: tabObj,
  };
}

export function getMarketGeoDataFromUrl(queryData) {
  const sub_id = queryData.sub_id ? [parseInt(queryData.sub_id)] : undefined;
  const market_cap = queryData.market_cap
    ? [].concat(queryData.market_cap).map((item) => parseInt(item))
    : [-1];
  const region_id = queryData.region_id
    ? [].concat(queryData.region_id).map((item) => parseInt(item))
    : queryData.country_id
      ? []
      : [-1];
  const country_id = queryData.country_id
    ? [].concat(queryData.country_id).map((item) => parseInt(item))
    : undefined;
  return {
    sub_id,
    market_cap,
    region_id,
    country_id,
  };
}
